/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Modal } from 'antd';
import { useMemo } from 'react';

import { Node, OperationalState } from '@/client';
import { OperationalState as OperationalStateBadge } from '@/components/States/OperationalState';
import { useBulkOperationalStateMutation } from '@/hooks/mutation/useBulkOperationalStateMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Space } from '@/primitives/Space';
import { Table } from '@/primitives/Table';
import { Title } from '@/primitives/Title';

export type ToggleStateModalProps = {
  selectedNodes: Node[];
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

type ToggleStatePreviewRow = {
  nodeId: string;
  hostname: string;
  currentState: OperationalState;
  requestedState: OperationalState;
};

export function ToggleStateModal({ selectedNodes, open, onClose, onSuccess }: ToggleStateModalProps) {
  const { t } = useMimicTranslation('nodes');
  const { mutate: bulkChangeOperationalState } = useBulkOperationalStateMutation({});

  const toggleStatePreview = useMemo<ToggleStatePreviewRow[]>(
    () =>
      selectedNodes.map((node) => {
        const currentState = node.appliedRevisionState?.operationalState ?? 'idle';
        const requestedState: OperationalState = currentState === 'active' ? 'idle' : 'active';
        return {
          nodeId: node.id,
          hostname: node.hostname ?? node.name ?? node.id,
          currentState,
          requestedState,
        };
      }),
    [selectedNodes],
  );

  const toggleStateColumns = useMemo(
    () => [
      {
        title: t('toggleStateModal.hostname'),
        dataIndex: 'hostname',
        key: 'hostname',
      },
      {
        title: t('toggleStateModal.requestedState'),
        dataIndex: 'requestedState',
        key: 'requestedState',
        render: (state: OperationalState) => <OperationalStateBadge state={state} />,
      },
    ],
    [t],
  );

  const handleOk = () => {
    const nodes = toggleStatePreview.map(({ nodeId, requestedState }) => ({
      nodeId,
      requestedOperationalState: requestedState,
    }));

    bulkChangeOperationalState({ nodes });
    onSuccess();
  };

  return (
    <Modal open={open} onCancel={onClose} destroyOnClose onOk={handleOk}>
      <Space dir="vertical" size="md" width="100%">
        <Title level={4}>{t('toggleStateModal.title')}</Title>
        <Table
          dataSource={toggleStatePreview}
          columns={toggleStateColumns}
          rowKey="nodeId"
          pagination={{ pageSize: 10 }}
        />
      </Space>
    </Modal>
  );
}
